import { useCallback, useEffect } from "react";

interface LoadFromLinkDialogProps {
  behind?: boolean;
  onExportAsImage: () => void;
  onSaveToDisk: () => void | Promise<void>;
  onReplace: () => void;
  onClose: () => void;
}

export default function LoadFromLinkDialog({
  behind = false,
  onExportAsImage,
  onSaveToDisk,
  onReplace,
  onClose,
}: LoadFromLinkDialogProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && !behind) onClose();
    },
    [onClose, behind],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [handleKeyDown]);

  return (
    <div
      style={{ ...overlayStyle, zIndex: behind ? 1 : 10000 }}
      onClick={onClose}
      role="dialog"
      aria-modal="false"
      data-app-usage-surface="import-export"
    >
      <div style={dialogStyle} onClick={(event) => event.stopPropagation()}>
        <h2 style={titleStyle}>Load from link</h2>
        <div style={warningCardStyle}>
          <div style={warningIconStyle}>⚠</div>
          <div style={warningTextStyle}>
            <strong>Loading external drawing will replace your existing content.</strong>
            <div style={{ marginTop: 6 }}>
              You can back up your drawing first by using one of the options below.
            </div>
          </div>
          <button type="button" onClick={onReplace} style={replaceButtonStyle}>
            Replace my content
          </button>
        </div>

        <div style={actionsGridStyle}>
          <BackupActionCard
            title="Export as image"
            description="Export the scene data as an image from which you can import later."
            buttonLabel="Export as image"
            onClick={onExportAsImage}
          />
          <BackupActionCard
            title="Save to disk"
            description="Export the scene data to a file from which you can import later."
            buttonLabel="Save to disk"
            onClick={onSaveToDisk}
          />
        </div>
      </div>
    </div>
  );
}

interface BackupActionCardProps {
  title: string;
  description: string;
  buttonLabel: string;
  onClick: () => void;
}

function BackupActionCard({ title, description, buttonLabel, onClick }: BackupActionCardProps) {
  return (
    <div style={cardStyle}>
      <h3 style={cardTitleStyle}>{title}</h3>
      <p style={cardDescriptionStyle}>{description}</p>
      <button type="button" onClick={onClick} style={cardButtonStyle}>
        {buttonLabel}
      </button>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 10000,
  background: "var(--exms-overlay-bg)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
};

const dialogStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 920,
  background: "var(--island-bg-color)",
  borderRadius: 12,
  border: "1px solid var(--dialog-border-color)",
  boxShadow: "var(--exms-shadow-modal)",
  padding: 24,
  fontFamily:
    "Assistant, system-ui, BlinkMacSystemFont, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 20,
  fontWeight: 700,
};

const warningCardStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 18,
  marginTop: 18,
  padding: 18,
  borderRadius: 12,
  background: "var(--exms-danger-soft)",
};

const warningIconStyle: React.CSSProperties = {
  width: 54,
  height: 54,
  borderRadius: "50%",
  background: "var(--exms-danger-soft)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 28,
  color: "var(--exms-danger-text)",
  flexShrink: 0,
};

const warningTextStyle: React.CSSProperties = {
  flex: 1,
  color: "var(--exms-danger-text)",
  fontSize: 16,
  lineHeight: 1.5,
};

const replaceButtonStyle: React.CSSProperties = {
  padding: "14px 22px",
  border: "none",
  borderRadius: 10,
  background: "var(--color-danger)",
  color: "var(--color-on-accent)",
  fontSize: 15,
  fontWeight: 700,
  cursor: "pointer",
  flexShrink: 0,
};

const actionsGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 24,
  marginTop: 28,
};

const cardStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  gap: 10,
};

const cardTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 18,
  fontWeight: 700,
};

const cardDescriptionStyle: React.CSSProperties = {
  margin: 0,
  color: "var(--color-text-secondary)",
  fontSize: 15,
  lineHeight: 1.45,
  minHeight: 66,
};

const cardButtonStyle: React.CSSProperties = {
  width: "100%",
  padding: "14px 18px",
  borderRadius: 10,
  border: "1px solid var(--color-border)",
  background: "var(--island-bg-color)",
  color: "var(--text-primary-color)",
  fontSize: 15,
  fontWeight: 600,
  cursor: "pointer",
};
